import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useVoiceEngine } from "@/hooks/useVoiceEngine";
import axios from "axios";
import { Mic, Send, Volume2, VolumeX, BookOpen, LogOut, Flame, Square, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ---- Sub-components ----

function DayMessages({ day, dayIdx, dimmed }) {
  const formatDayLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (d.toDateString() === today.toDateString()) return 'Today';
      if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div>
      <div data-testid={`day-marker-${day.date}`} className="flex items-center gap-3 py-3">
        <div className="flex-1 h-px" style={{ background: '#2e2a26' }} />
        <span style={{ color: '#6a6560', fontSize: '0.7rem', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {formatDayLabel(day.date)}
        </span>
        <div className="flex-1 h-px" style={{ background: '#2e2a26' }} />
      </div>
      {day.messages.map((msg, i) => (
        <div key={`${day.date}-${msg.role}-${i}`} data-testid={`month-message-${msg.role}-${dayIdx}-${i}`}
          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} mb-3`}
          style={{ opacity: dimmed ? 0.5 : 1 }}>
          <div className={msg.role === "echo" ? "chat-bubble-echo" : "chat-bubble-user"}>
            {msg.content}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusIndicator({ isSpeaking, isListening, isProcessing, micActive }) {
  if (isSpeaking) {
    return (
      <div data-testid="speaking-indicator" className="flex items-center gap-3 fade-in">
        <div className="waveform">{[...Array(5)].map((_, i) => <div key={`wave-${i}`} className="waveform-bar" />)}</div>
        <span style={{ color: '#c4956a', fontSize: '0.8rem' }}>ECHO is speaking</span>
        {micActive && <span style={{ color: '#3a3530', fontSize: '0.7rem' }}>speak to interrupt</span>}
      </div>
    );
  }
  if (isListening) {
    return (
      <div data-testid="recording-indicator" className="flex items-center gap-3 fade-in">
        <div className="waveform">{[...Array(5)].map((_, i) => <div key={`wave-${i}`} className="waveform-bar" />)}</div>
        <span style={{ color: '#c4956a', fontSize: '0.8rem' }}>Listening...</span>
      </div>
    );
  }
  if (isProcessing) {
    return <span data-testid="processing-indicator" style={{ color: '#6a6560', fontSize: '0.8rem' }} className="fade-in">Thinking...</span>;
  }
  if (micActive) {
    return <span style={{ color: '#3a3530', fontSize: '0.75rem' }}>Mic active. Speak anytime.</span>;
  }
  return <span style={{ color: '#3a3530', fontSize: '0.75rem' }}>Tap mic to start conversation</span>;
}

// ---- Main Component ----

export default function JournalPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [streak, setStreak] = useState(0);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [userMsgCount, setUserMsgCount] = useState(0);
  const [loadingSession, setLoadingSession] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  const [monthMessages, setMonthMessages] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [viewingMonthKey, setViewingMonthKey] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });

  const currentMonthDaysRef = useRef([]);
  const messagesEndRef = useRef(null);
  const sessionRef = useRef(null);
  const isOnboardingRef = useRef(false);
  const voiceEnabledRef = useRef(true);

  const currentMonthKey = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  })();
  const isArchiveView = viewingMonthKey !== currentMonthKey;

  // Keep refs in sync
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { isOnboardingRef.current = isOnboarding; }, [isOnboarding]);
  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);

  // Voice callbacks
  const handleUserMessage = useCallback((text) => {
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
  }, []);

  const handleEchoMessage = useCallback((text) => {
    setMessages(prev => [...prev, { role: "echo", content: text, timestamp: new Date().toISOString() }]);
    setUserMsgCount(prev => prev + 1);
  }, []);

  const handleVoiceError = useCallback(() => { /* Error logged in hook */ }, []);

  // Voice engine hook
  const voice = useVoiceEngine({
    sessionRef,
    isOnboardingRef,
    voiceEnabledRef,
    onUserMessage: handleUserMessage,
    onEchoMessage: handleEchoMessage,
    onError: handleVoiceError,
  });

  // Scroll to bottom on message changes
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages, monthMessages, voice.liveTranscript, scrollToBottom]);

  // ---- Session Init ----

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const [sessionRes, monthsRes] = await Promise.all([
          axios.post(`${API}/api/chat/start-session`, {}, { withCredentials: true }),
          axios.get(`${API}/api/chat/months`, { withCredentials: true })
        ]);
        if (cancelled) return;
        setSession(sessionRes.data);
        setMessages(sessionRes.data.messages || []);
        setMonthMessages(sessionRes.data.month_messages || []);
        currentMonthDaysRef.current = sessionRes.data.month_messages || [];
        setStreak(sessionRes.data.streak || 0);
        setIsOnboarding(sessionRes.data.is_onboarding || false);
        setAvailableMonths(monthsRes.data || []);
        setUserMsgCount((sessionRes.data.messages || []).filter(m => m.role === "user").length);
      } catch (err) {
        if (!cancelled) { /* Session start failed — will show empty state */ }
      } finally {
        if (!cancelled) setLoadingSession(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Onboarding ----

  const completeOnboarding = async () => {
    voice.stopAll();
    try {
      const res = await axios.post(`${API}/api/user/complete-onboarding`, {}, { withCredentials: true });
      setUser(res.data);
      setIsOnboarding(false);
      const [sessionRes, monthsRes] = await Promise.all([
        axios.post(`${API}/api/chat/start-session`, {}, { withCredentials: true }),
        axios.get(`${API}/api/chat/months`, { withCredentials: true })
      ]);
      setSession(sessionRes.data);
      setMessages(sessionRes.data.messages || []);
      setMonthMessages(sessionRes.data.month_messages || []);
      currentMonthDaysRef.current = sessionRes.data.month_messages || [];
      setStreak(sessionRes.data.streak || 0);
      setAvailableMonths(monthsRes.data || []);
    } catch (err) {
      /* Onboarding completion failed — user can retry */
    }
  };

  // ---- Text Send ----

  const sendText = async () => {
    if (!textInput.trim() || !sessionRef.current) return;
    voice.setIsProcessing(true);
    const text = textInput.trim();
    setTextInput("");
    handleUserMessage(text);

    try {
      const form = new FormData();
      form.append("text", text);
      form.append("session_id", sessionRef.current.id);
      form.append("is_onboarding", String(isOnboardingRef.current));
      form.append("voice_enabled", String(voiceEnabledRef.current));
      const res = await axios.post(`${API}/api/chat/respond`, form, { withCredentials: true });
      handleEchoMessage(res.data.ai_text);
      voice.setIsProcessing(false);
      if (res.data.audio && voiceEnabledRef.current) voice.playAudio(res.data.audio);
    } catch (err) {
      voice.setIsProcessing(false);
      /* Send failed — message shown, user can retry */
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  const handleLogout = async () => {
    voice.stopAll();
    await logout();
    navigate("/");
  };

  // ---- Month Navigation ----

  const formatMonthLabel = (mk) => {
    const [y, m] = mk.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigableKeys = (() => {
    const keys = new Set(availableMonths.map(m => m.key));
    keys.add(currentMonthKey);
    return [...keys].sort();
  })();

  const viewIdx = navigableKeys.indexOf(viewingMonthKey);
  const hasPrevMonth = viewIdx > 0;
  const hasNextMonth = viewIdx < navigableKeys.length - 1;

  const navigateMonth = async (direction) => {
    voice.stopSpeaking();
    const newIdx = viewIdx + direction;
    if (newIdx < 0 || newIdx >= navigableKeys.length) return;
    const newKey = navigableKeys[newIdx];
    setViewingMonthKey(newKey);

    if (newKey === currentMonthKey) {
      setMonthMessages(currentMonthDaysRef.current);
    } else {
      const [y, m] = newKey.split('-').map(Number);
      try {
        const res = await axios.get(`${API}/api/chat/month/${y}/${m}`, { withCredentials: true });
        setMonthMessages(res.data.days || []);
      } catch (err) {
        setMonthMessages([]);
      }
    }
  };

  // ---- Loading ----

  if (loadingSession) {
    return (
      <div data-testid="journal-loading" className="min-h-screen flex items-center justify-center" style={{ background: '#141210' }}>
        <div className="text-center">
          <p className="echo-logo" style={{ fontSize: '1.5rem' }}>ECHO</p>
          <div className="mt-4 w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#c4956a', borderTopColor: 'transparent' }} />
          <p className="mt-3" style={{ color: '#6a6560', fontSize: '0.85rem' }}>Starting your session...</p>
        </div>
      </div>
    );
  }

  // ---- Render ----

  return (
    <div data-testid="journal-page" className="min-h-screen flex flex-col" style={{ background: '#141210' }}>
      {/* Header */}
      <header className="echo-nav">
        <Link to="/" className="echo-logo" style={{ textDecoration: 'none' }}>ECHO</Link>
        <div className="flex items-center gap-3">
          <div data-testid="month-picker" className="flex items-center gap-0.5">
            <button data-testid="month-prev-btn" onClick={() => navigateMonth(-1)} disabled={!hasPrevMonth}
              className="p-1 rounded" style={{ color: hasPrevMonth ? '#9a9590' : '#2e2a26', background: 'none', border: 'none', cursor: hasPrevMonth ? 'pointer' : 'default' }}>
              <ChevronLeft size={16} />
            </button>
            <span data-testid="month-label" style={{ color: isArchiveView ? '#6a6560' : '#c4956a', fontSize: '0.8rem', fontFamily: "'Outfit', sans-serif", minWidth: '110px', textAlign: 'center' }}>
              {formatMonthLabel(viewingMonthKey)}
            </span>
            <button data-testid="month-next-btn" onClick={() => navigateMonth(1)} disabled={!hasNextMonth}
              className="p-1 rounded" style={{ color: hasNextMonth ? '#9a9590' : '#2e2a26', background: 'none', border: 'none', cursor: hasNextMonth ? 'pointer' : 'default' }}>
              <ChevronRight size={16} />
            </button>
          </div>

          {streak > 1 && !isArchiveView && (
            <span data-testid="streak-counter" className="flex items-center gap-1" style={{ color: '#c4956a', fontSize: '0.85rem' }}>
              <Flame size={16} /> {streak}
            </span>
          )}

          {!isArchiveView && (
            <button data-testid="voice-toggle-btn"
              onClick={() => { setVoiceEnabled(!voiceEnabled); if (voice.isSpeaking) voice.stopSpeaking(); }}
              className="p-2 rounded-full" style={{ color: voiceEnabled ? '#c4956a' : '#6a6560', background: 'transparent', border: 'none', cursor: 'pointer' }}
              title={voiceEnabled ? "Voice ON" : "Voice OFF"}>
              {voiceEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
          )}

          <Link to="/timeline" data-testid="timeline-link" className="p-2 rounded-full" style={{ color: '#9a9590' }}><BookOpen size={18} /></Link>
          <button data-testid="logout-btn" onClick={handleLogout} className="p-2 rounded-full" style={{ color: '#6a6560', background: 'transparent', border: 'none', cursor: 'pointer' }}><LogOut size={18} /></button>
        </div>
      </header>

      {isArchiveView && (
        <div data-testid="archive-indicator" className="fixed top-14 left-0 right-0 z-10 text-center py-2" style={{ background: 'rgba(20,18,16,0.95)', borderBottom: '1px solid #2e2a26' }}>
          <span style={{ color: '#6a6560', fontSize: '0.75rem', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase' }}>Archive &mdash; read only</span>
        </div>
      )}

      {/* Messages */}
      <main className="flex-1 overflow-y-auto pb-48 px-4" style={{ paddingTop: isArchiveView ? '6.5rem' : '5rem' }}>
        <div className="max-w-2xl mx-auto space-y-3">
          {monthMessages.map((day, dayIdx) => (
            <DayMessages key={`day-${day.date}`} day={day} dayIdx={dayIdx} dimmed={!isArchiveView} />
          ))}

          {!isArchiveView && messages.length > 0 && (
            <>
              {monthMessages.length > 0 && (
                <div data-testid="today-separator" className="flex items-center gap-3 py-3">
                  <div className="flex-1 h-px" style={{ background: 'rgba(196,149,106,0.3)' }} />
                  <span style={{ color: '#c4956a', fontSize: '0.7rem', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>Today</span>
                  <div className="flex-1 h-px" style={{ background: 'rgba(196,149,106,0.3)' }} />
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={`msg-${msg.role}-${msg.timestamp || i}`} data-testid={`message-${msg.role}-${i}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} fade-in`}
                  style={{ animationDelay: `${Math.min(i * 0.05, 0.3)}s` }}>
                  <div className={msg.role === "echo"
                    ? `chat-bubble-echo${voice.isSpeaking && i === messages.length - 1 ? " speaking-glow" : ""}`
                    : "chat-bubble-user"}>
                    {msg.content}
                  </div>
                </div>
              ))}
            </>
          )}

          {!isArchiveView && voice.liveTranscript && (
            <div data-testid="live-transcript" className="flex justify-end fade-in">
              <div className="chat-bubble-user" style={{ opacity: 0.6 }}>
                {voice.liveTranscript}
                <span className="inline-block ml-1 w-1.5 h-4 animate-pulse" style={{ background: '#c4956a', verticalAlign: 'middle' }} />
              </div>
            </div>
          )}

          {isArchiveView && monthMessages.length === 0 && (
            <div data-testid="empty-archive" className="text-center py-16">
              <p style={{ color: '#6a6560', fontSize: '0.9rem', fontFamily: "'Outfit', sans-serif" }}>No conversations this month</p>
            </div>
          )}

          {voice.isProcessing && !isArchiveView && (
            <div data-testid="thinking-indicator" className="flex justify-start">
              <div className="chat-bubble-echo">
                <div className="flex gap-1">
                  {[0, 0.15, 0.3].map(delay => (
                    <span key={`dot-${delay}`} className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#c4956a', animationDelay: `${delay}s` }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </main>

      {!isArchiveView && isOnboarding && userMsgCount >= 3 && (
        <div data-testid="onboarding-cta" className="fixed bottom-44 left-0 right-0 flex justify-center z-10 fade-in">
          <button onClick={completeOnboarding} className="btn-amber">Begin my archive <span style={{ marginLeft: '4px' }}>&rarr;</span></button>
        </div>
      )}

      {!isArchiveView && (
        <footer className="fixed bottom-0 left-0 right-0 z-20" style={{ background: 'linear-gradient(transparent, #141210 30%)', paddingTop: '2rem' }}>
          <div className="max-w-2xl mx-auto px-4 pb-6">
            <div className="flex items-center justify-center gap-3 mb-4 min-h-[28px]">
              <StatusIndicator isSpeaking={voice.isSpeaking} isListening={voice.isListening} isProcessing={voice.isProcessing} micActive={voice.micActive} />
            </div>

            <div className="flex items-center gap-3">
              <button data-testid="mic-btn" onClick={voice.toggleMic}
                disabled={voice.isProcessing}
                className={`mic-btn flex-shrink-0 ${voice.micActive ? "recording" : ""}`}
                style={{ opacity: voice.isProcessing ? 0.5 : 1 }}
                title={voice.micActive ? "End conversation" : "Start conversation"}>
                {voice.micActive ? <Square size={22} fill="#c4956a" /> : <Mic size={24} />}
              </button>

              <div className="flex-1 flex items-center gap-2" style={{ background: 'rgba(26,24,20,0.8)', border: '1px solid #2e2a26', borderRadius: '24px', padding: '0.5rem 0.75rem 0.5rem 1rem' }}>
                <input data-testid="text-input" type="text" value={textInput}
                  onChange={e => setTextInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Or type here..."
                  disabled={voice.isProcessing || voice.isListening}
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#f5f0e8', fontFamily: "'Outfit', sans-serif", fontSize: '16px' }} />
                <button data-testid="send-btn" onClick={sendText}
                  disabled={!textInput.trim() || voice.isProcessing}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: textInput.trim() ? '#c4956a' : '#3a3530', padding: '4px' }}>
                  <Send size={18} />
                </button>
              </div>
            </div>

            {voice.micPermission === "denied" && (
              <p className="text-center mt-2" style={{ color: '#f87171', fontSize: '0.75rem' }}>
                Microphone access denied. Please enable it in your browser settings.
              </p>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
